import { useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Bot, Shield, AlertTriangle, Eye, Activity } from "lucide-react"

interface BotActivity {
  id: string | number
  ip: string
  action: 'blocked' | 'challenged' | 'monitored'
  type: 'stealth_detection' | 'fingerprint_reuse' | 'rate_limit' | string
  pattern: string
  score: number
  timestamp: string | number | Date
}

interface BotDetectionContentProps {
  botActivity?: BotActivity[]
}

export function BotDetectionContent({ botActivity = [] }: BotDetectionContentProps) {
  // Ensure botActivity is always an array and handle edge cases
  const safeActivity = useMemo(() => {
    if (!botActivity) return []
    if (!Array.isArray(botActivity)) return []
    return botActivity.filter(activity => activity && typeof activity === 'object')
  }, [botActivity])

  const detectionStats = {
    totalDetections: 1247,
    stealthBots: 342,
    fingerprintCollisions: 156,
    rateLimitViolations: 89,
    headlessBrowsers: 234,
    automationTools: 426,
  }

  const detectionPatterns = [
    { name: "Puppeteer Detection", count: 156, severity: "high" as const, description: "Headless Chrome automation detected" },
    { name: "Playwright Signature", count: 89, severity: "high" as const, description: "Playwright automation framework" },
    { name: "Selenium WebDriver", count: 67, severity: "medium" as const, description: "Selenium automation detected" },
    { name: "Fingerprint Reuse", count: 234, severity: "medium" as const, description: "Same fingerprint from multiple IPs" },
    { name: "Rapid Navigation", count: 123, severity: "low" as const, description: "Unusually fast page navigation" },
    { name: "Missing Headers", count: 178, severity: "medium" as const, description: "Suspicious header patterns" },
  ]

  // Sample data for demonstration if no real data is provided
  const sampleActivity: BotActivity[] = [
    {
      id: '1',
      ip: '192.168.1.100',
      action: 'blocked',
      type: 'stealth_detection',
      pattern: 'Puppeteer Signature',
      score: 95,
      timestamp: new Date().toISOString()
    },
    {
      id: '2',
      ip: '10.0.0.50',
      action: 'challenged',
      type: 'fingerprint_reuse',
      pattern: 'Duplicate Fingerprint',
      score: 78,
      timestamp: new Date(Date.now() - 300000).toISOString()
    },
    {
      id: '3',
      ip: '172.16.0.25',
      action: 'monitored',
      type: 'rate_limit',
      pattern: 'Rapid Requests',
      score: 65,
      timestamp: new Date(Date.now() - 600000).toISOString()
    }
  ]

  // Use sample data if no real activity is provided
  const displayActivity = safeActivity.length > 0 ? safeActivity : sampleActivity

  return (
    <div className="space-y-6">
      {/* Detection Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-900/50 border-red-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Bot className="h-6 w-6 text-red-500" />
              <Badge className="bg-red-500/20 text-red-400 border-red-500/50">Critical</Badge>
            </div>
            <div className="text-2xl font-bold text-red-400">{detectionStats.totalDetections}</div>
            <div className="text-sm text-slate-400">Total Bot Detections</div>
            <div className="text-xs text-slate-500 mt-1">Last 24 hours</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-amber-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Eye className="h-6 w-6 text-amber-500" />
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">Stealth</Badge>
            </div>
            <div className="text-2xl font-bold text-amber-400">{detectionStats.stealthBots}</div>
            <div className="text-sm text-slate-400">Stealth Bots</div>
            <div className="text-xs text-slate-500 mt-1">Advanced evasion attempts</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-blue-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Shield className="h-6 w-6 text-blue-500" />
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">Protected</Badge>
            </div>
            <div className="text-2xl font-bold text-blue-400">98.7%</div>
            <div className="text-sm text-slate-400">Detection Rate</div>
            <div className="text-xs text-slate-500 mt-1">System accuracy</div>
          </CardContent>
        </Card>
      </div>

      {/* Detection Patterns */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Detection Patterns & Signatures
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {detectionPatterns.map((pattern, index) => (
              <div key={`pattern-${index}`} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-slate-200">{pattern.name}</h3>
                    <Badge
                      className={
                        pattern.severity === "high"
                          ? "bg-red-500/20 text-red-400 border-red-500/50"
                          : pattern.severity === "medium"
                            ? "bg-amber-500/20 text-amber-400 border-amber-500/50"
                            : "bg-blue-500/20 text-blue-400 border-blue-500/50"
                      }
                    >
                      {pattern.severity}
                    </Badge>
                  </div>
                  <div className="text-xl font-bold text-cyan-400">{pattern.count}</div>
                </div>
                <p className="text-sm text-slate-400 mb-3">{pattern.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-4">
                    <Progress value={(pattern.count / detectionStats.totalDetections) * 100} className="h-2" />
                  </div>
                  <div className="flex space-x-2">
                    <Button size="sm" variant="ghost" className="h-7 px-3 text-xs text-slate-300 hover:text-white">
                      Analyze
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-3 text-xs text-slate-300 hover:text-white">
                      Block Pattern
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Real-time Bot Activity */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2">
            <Activity className="h-5 w-5 text-cyan-500" />
            Real-time Bot Activity Feed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="recent" className="w-full">
            <TabsList className="bg-slate-800/50 p-1 mb-4">
              <TabsTrigger
                value="recent"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                Recent Activity
              </TabsTrigger>
              <TabsTrigger
                value="blocked"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                Blocked
              </TabsTrigger>
              <TabsTrigger
                value="challenged"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                Challenged
              </TabsTrigger>
            </TabsList>

            <TabsContent value="recent" className="mt-0">
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {displayActivity.length > 0 ? (
                  displayActivity.slice(0, 10).map((activity, index) => (
                    <BotActivityCard 
                      key={activity.id || `activity-${index}`} 
                      activity={activity} 
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No recent bot activity detected</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="blocked" className="mt-0">
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {displayActivity
                  .filter((a) => a?.action === "blocked")
                  .slice(0, 10)
                  .map((activity, index) => (
                    <BotActivityCard 
                      key={activity.id || `blocked-${index}`} 
                      activity={activity} 
                    />
                  ))}
              </div>
            </TabsContent>

            <TabsContent value="challenged" className="mt-0">
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {displayActivity
                  .filter((a) => a?.action === "challenged")
                  .slice(0, 10)
                  .map((activity, index) => (
                    <BotActivityCard 
                      key={activity.id || `challenged-${index}`} 
                      activity={activity} 
                    />
                  ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

interface BotActivityCardProps {
  activity: BotActivity
}

function BotActivityCard({ activity }: BotActivityCardProps) {
  const getTypeColor = (type: string) => {
    switch (type) {
      case "stealth_detection":
        return "text-red-400"
      case "fingerprint_reuse":
        return "text-amber-400"
      case "rate_limit":
        return "text-blue-400"
      default:
        return "text-slate-400"
    }
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case "blocked":
        return "bg-red-500/20 text-red-400 border-red-500/50"
      case "challenged":
        return "bg-amber-500/20 text-amber-400 border-amber-500/50"
      case "monitored":
        return "bg-blue-500/20 text-blue-400 border-blue-500/50"
      default:
        return "bg-slate-500/20 text-slate-400 border-slate-500/50"
    }
  }

  // Safe timestamp handling
  const formatTimestamp = (timestamp: string | number | Date) => {
    try {
      return new Date(timestamp).toLocaleTimeString()
    } catch {
      return "Invalid time"
    }
  }

  // Ensure activity object has all required properties
  const safeActivity = {
    ip: activity?.ip || 'Unknown IP',
    action: activity?.action || 'unknown',
    pattern: activity?.pattern || 'Unknown Pattern',
    type: activity?.type || 'unknown',
    score: activity?.score || 0,
    timestamp: activity?.timestamp || new Date()
  }

  return (
    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 hover:bg-slate-800/70 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Bot className="h-4 w-4 text-red-400" />
          <span className="text-sm font-mono text-slate-300">{safeActivity.ip}</span>
        </div>
        <Badge className={`${getActionColor(safeActivity.action)} text-xs`}>
          {safeActivity.action}
        </Badge>
      </div>

      <div className="mb-3">
        <div className={`text-sm font-medium ${getTypeColor(safeActivity.type)}`}>
          {safeActivity.pattern}
        </div>
        <div className="text-xs text-slate-500 capitalize">
          {safeActivity.type.replace("_", " ")}
        </div>
      </div>

      <div className="flex items-center justify-between text-xs">
        <div className="text-slate-500">
          Threat Score: <span className="text-red-400 font-bold">{safeActivity.score}</span>
        </div>
        <div className="text-slate-500">{formatTimestamp(safeActivity.timestamp)}</div>
      </div>

      <div className="mt-3 pt-2 border-t border-slate-700/50 flex justify-between items-center">
        <div className="text-xs text-slate-500">Pattern: {safeActivity.pattern}</div>
        <div className="flex space-x-1">
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-slate-300 hover:text-white">
            Details
          </Button>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-slate-300 hover:text-white">
            Whitelist
          </Button>
        </div>
      </div>
    </div>
  )
}

export default BotDetectionContent
